// Phase 10 — Per-page SEO metadata.
//
// Single source of truth for:
//   • <SeoHead/> props on every public marketing page.
//   • frontend/scripts/build_sitemap.js (sitemap.xml regeneration at
//     build time).
//
// Every entry MUST carry: path, title (≤ 60 chars), description
// (≤ 160 chars), and a crawlAllowed flag. Entries that should not
// appear in the sitemap or in the indexable surface (legal policies
// can still be crawled; 404 must not) are marked inSitemap: false.
//
// Keep this file free of React imports — it is imported by a Node
// build-time script too.

/** Domain placeholder — substituted at deploy time via the platform
 *  token replacement. The post-deploy sanity script greps for this
 *  literal string and fails the release if it survives to prod. */
export const PRODUCTION_DOMAIN_PLACEHOLDER = "REPLACE_WITH_PRODUCTION_DOMAIN";

/** Helper — absolute URL for a given public path. */
export function absoluteUrl(path) {
    const clean = path === "/" ? "" : path.replace(/\/+$/, "");
    return `https://${PRODUCTION_DOMAIN_PLACEHOLDER}${clean}`;
}

/** Ordered list of every public marketing page. */
export const SEO_PAGES = [
    {
        path: "/",
        id: "home",
        title: "UAVSimNet — Counter-UAS Simulation Platform | The RLK Group",
        description:
            "Counter-UAS simulation for defence procurement. Validated interceptor models, JSBSim flight dynamics, design optimisation workbench, evidence-grade reporting. UK-built by The RLK Group.",
        ogImage: "/og-image.png",
        primaryKeywords: [
            "counter-UAS simulation platform",
            "counter-UAS simulation",
            "drone defence procurement",
            "defence simulation software UK",
            "design optimisation workbench",
        ],
        secondaryKeywords: [
            "JSBSim counter-drone",
            "6-DoF drone simulation",
            "high-fidelity drone interception",
            "interceptor mission analysis",
            "counter-UAS design recommendations",
            "probability of kill",
            "Pareto trade-off",
            "sensitivity analysis",
        ],
        changefreq: "weekly",
        priority: 1.0,
        inSitemap: true,
    },
    {
        path: "/features",
        id: "features",
        title: "UAVSimNet Features — Mission Console, Calibration, Evidence",
        description:
            "Mission validation, platform trade studies, operator rehearsal, decision evidence, design optimisation, and an audit-ready record. Counter-UAS outcomes in one operator console.",
        ogImage: "/og-image.png",
        primaryKeywords: [
            "counter-UAS simulation features",
            "Monte Carlo intercept analysis",
            "interceptor calibration platform",
            "design optimisation workbench",
        ],
        secondaryKeywords: [
            "validated drone interceptor model",
            "counter-UAS evidence management",
            "Pareto trade-off",
            "sensitivity analysis",
            "probability of kill",
        ],
        changefreq: "monthly",
        priority: 0.9,
        inSitemap: true,
    },
    {
        path: "/features#design-optimisation",
        id: "features-design-optimisation",
        title: "UAVSimNet — Design Optimisation Workbench",
        description:
            "Forward and inverse simulation for counter-UAS design optimisation. Sensitivity ranking, candidate exploration, Pareto trade-offs, and confirmation runs that lift probability of kill within engineering constraints.",
        ogImage: "/og-image.png",
        primaryKeywords: [
            "design optimisation workbench",
            "counter-UAS design recommendations",
            "probability of kill",
            "Pareto trade-off",
            "sensitivity analysis",
        ],
        secondaryKeywords: [
            "interceptor design recommendations",
            "Monte Carlo confirmation runs",
            "design-cost vs performance trade-off",
        ],
        changefreq: "monthly",
        priority: 0.85,
        inSitemap: false,
    },
    {
        path: "/use-cases",
        id: "use-cases",
        title: "Counter-UAS Simulation Use Cases — Procurement & Engineering",
        description:
            "How procurement offices, capability teams, and operator rehearsal squads use UAVSimNet to de-risk interceptor trade studies, sizing, and live-fire windows.",
        ogImage: "/og-image.png",
        primaryKeywords: [
            "counter-UAS simulation use cases",
            "drone defence procurement evaluation tool",
            "interceptor mission analysis",
        ],
        secondaryKeywords: [
            "drone interceptor flight dynamics",
            "operator rehearsal",
        ],
        changefreq: "monthly",
        priority: 0.9,
        inSitemap: true,
    },
    {
        path: "/evidence",
        id: "evidence",
        title: "UAVSimNet Credibility Framework — Evidence, Not Slides",
        description:
            "How UAVSimNet's model registry, regression harness, and validity envelopes make every counter-UAS claim defensible in a procurement review.",
        ogImage: "/og-image.png",
        primaryKeywords: [
            "simulation credibility framework",
            "counter-UAS evidence management",
            "validated drone interceptor model",
        ],
        secondaryKeywords: [
            "Monte Carlo intercept analysis",
            "replay determinism",
        ],
        changefreq: "monthly",
        priority: 0.9,
        inSitemap: true,
    },
    {
        path: "/demo",
        id: "demo",
        title: "UAVSimNet Demos — Live Counter-UAS Simulations",
        description:
            "Six verified counter-UAS intercepts from the same engine that runs customer studies. Real runs, quantified outcomes, miss distance, trajectories.",
        ogImage: "/og-image.png",
        primaryKeywords: [
            "counter-UAS simulation demo",
            "drone interception simulation",
            "high-fidelity drone interception",
        ],
        secondaryKeywords: [
            "Monte Carlo intercept analysis",
            "drone interceptor flight dynamics",
        ],
        changefreq: "weekly",
        priority: 0.8,
        inSitemap: true,
    },
    {
        path: "/security",
        id: "security",
        title: "UAVSimNet Security — Multi-tenant, Audit, Compliance",
        description:
            "Per-customer tenant isolation, invite-only authenticated access, auditable event trail, and export-control posture for defence and licensed-industry teams.",
        ogImage: "/og-image.png",
        primaryKeywords: [
            "counter-UAS platform security",
            "defence simulation software UK",
        ],
        secondaryKeywords: [
            "tenant isolation",
            "audit trail",
            "compliance posture",
        ],
        changefreq: "monthly",
        priority: 0.8,
        inSitemap: true,
    },
    {
        path: "/about",
        id: "about",
        title: "About UAVSimNet — Built by The RLK Group",
        description:
            "UAVSimNet is built by The RLK Group, a UK-registered defence engineering consultancy (SC781391). Mission, team posture, and origin of the platform.",
        ogImage: "/og-image.png",
        primaryKeywords: [
            "about UAVSimNet",
            "The RLK Group",
            "defence simulation software UK",
        ],
        secondaryKeywords: [
            "defence engineering consultancy",
            "Scottish defence-tech",
        ],
        changefreq: "monthly",
        priority: 0.7,
        inSitemap: true,
    },
    {
        path: "/faq",
        id: "faq",
        title: "UAVSimNet FAQ — Counter-UAS Simulation Questions",
        description:
            "Frequently asked questions about UAVSimNet: how scenarios compile, JSBSim vs kinematic, Monte Carlo sizing, procurement evidence, deployment, onboarding.",
        ogImage: "/og-image.png",
        primaryKeywords: [
            "UAVSimNet FAQ",
            "counter-UAS simulation questions",
        ],
        secondaryKeywords: [
            "JSBSim counter-drone",
            "Monte Carlo intercept analysis",
            "onboarding",
        ],
        changefreq: "monthly",
        priority: 0.7,
        inSitemap: true,
    },
    {
        path: "/docs",
        id: "docs",
        title: "UAVSimNet Docs — Counter-UAS Simulation Documentation",
        description:
            "Public documentation for UAVSimNet — capability tour, architecture story, evidence framework, and use cases for counter-UAS procurement and engineering.",
        ogImage: "/og-image.png",
        primaryKeywords: [
            "UAVSimNet documentation",
            "counter-UAS simulation documentation",
        ],
        secondaryKeywords: ["capability tour", "architecture", "evidence framework"],
        changefreq: "monthly",
        priority: 0.6,
        inSitemap: true,
    },
    {
        path: "/docs/features",
        id: "docs-features",
        title: "UAVSimNet Docs — What the platform does",
        description:
            "Counter-UAS capability tour: airframes, environment fidelity, RF line-of-sight, threat modelling, guidance laws, engagement / lethality, reviewer sign-off.",
        ogImage: "/og-image.png",
        primaryKeywords: ["counter-UAS simulation features"],
        secondaryKeywords: ["engagement model", "guidance laws", "reviewer workflow"],
        changefreq: "monthly",
        priority: 0.55,
        inSitemap: true,
    },
    {
        path: "/docs/how-it-works",
        id: "docs-how-it-works",
        title: "UAVSimNet Docs — How it works",
        description:
            "Deterministic Monte Carlo, JSBSim-backed dynamics, validation gate, immutable parameter-provenance snapshot, reviewer state machine.",
        ogImage: "/og-image.png",
        primaryKeywords: ["counter-UAS simulation architecture"],
        secondaryKeywords: ["deterministic Monte Carlo", "JSBSim", "validation gate"],
        changefreq: "monthly",
        priority: 0.55,
        inSitemap: true,
    },
    {
        path: "/docs/evidence",
        id: "docs-evidence",
        title: "UAVSimNet Docs — Evidence & credibility",
        description:
            "Validated-vs-provisional registry, citations, bit-exact replay, JSBSim heritage, reviewer chain, and immutable run snapshots.",
        ogImage: "/og-image.png",
        primaryKeywords: ["simulation credibility framework"],
        secondaryKeywords: ["validated model registry", "reproducibility", "audit trail"],
        changefreq: "monthly",
        priority: 0.55,
        inSitemap: true,
    },
    {
        path: "/docs/use-cases",
        id: "docs-use-cases",
        title: "UAVSimNet Docs — Counter-UAS use cases",
        description:
            "Three narrative counter-UAS scenarios — urban-canyon net capture, contested-weather salvo engagement, group-1 swarm vs kinetic interceptor.",
        ogImage: "/og-image.png",
        primaryKeywords: ["counter-UAS simulation use cases"],
        secondaryKeywords: ["urban intercept", "swarm defeat", "weather degraded engagement"],
        changefreq: "monthly",
        priority: 0.55,
        inSitemap: true,
    },
    {
        path: "/contact",
        id: "contact",
        title: "Contact UAVSimNet — Request Access",
        description:
            "Request access to UAVSimNet. Defence, government, and licensed-industry teams only. Single-use invite links; onboarding within five business days.",
        ogImage: "/og-image.png",
        primaryKeywords: [
            "contact UAVSimNet",
            "request access",
            "drone defence procurement evaluation tool",
        ],
        secondaryKeywords: ["onboarding", "tenant provisioning"],
        changefreq: "monthly",
        priority: 0.6,
        inSitemap: true,
    },
    {
        path: "/terms",
        id: "terms",
        title: "UAVSimNet Terms of Service",
        description:
            "Terms of Service for UAVSimNet, the counter-UAS simulation platform issued by The RLK Group (SC781391). Governing law: Scotland.",
        ogImage: "/og-image.png",
        primaryKeywords: [],
        secondaryKeywords: [],
        changefreq: "yearly",
        priority: 0.3,
        inSitemap: true,
        legal: true,
    },
    {
        path: "/privacy",
        id: "privacy",
        title: "UAVSimNet Privacy Notice",
        description:
            "Privacy notice for UAVSimNet under UK GDPR / DPA 2018. The RLK Group is the data controller. Company number SC781391.",
        ogImage: "/og-image.png",
        primaryKeywords: [],
        secondaryKeywords: [],
        changefreq: "yearly",
        priority: 0.3,
        inSitemap: true,
        legal: true,
    },
    {
        path: "/aup",
        id: "aup",
        title: "UAVSimNet Acceptable Use Policy",
        description:
            "Acceptable Use Policy for UAVSimNet. Eligibility, permitted use, prohibited use, and export-control compliance under UK Strategic Export Controls.",
        ogImage: "/og-image.png",
        primaryKeywords: [],
        secondaryKeywords: [],
        changefreq: "yearly",
        priority: 0.3,
        inSitemap: true,
        legal: true,
    },
];

/** Look up a page by path (used by <SeoHead/> when a page doesn't pass
 *  explicit props). */
export function getPageMeta(path) {
    const clean = path === "/" ? "/" : path.replace(/\/+$/, "");
    return SEO_PAGES.find((p) => p.path === clean) || null;
}
